import React from 'react';
import { motion } from 'framer-motion';
import { CircleCheckBig } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.45, ease: 'easeOut' } })
};

/**
 * TrustBenefitsSection
 * Editorial, typography-led trust section for landing pages.
 * Intentionally avoids cards and icons (used heavily elsewhere).
 *
 * Props:
 *  - eyebrow: small uppercase label (brand blue)
 *  - headline: large editorial headline (supports JSX/<br />)
 *  - lead: 1–2 paragraphs of hero copy (string or array of strings)
 *  - benefits: [{ title, desc }] — rendered as numbered editorial rows
 *  - testId: optional data-testid root override
 */
export const TrustBenefitsSection = ({
  eyebrow = 'Why work with us',
  headline,
  lead,
  benefits = [],
  testId = 'trust-benefits-section',
  marker = 'number', // 'number' | 'check'
}) => {
  const leadParagraphs = Array.isArray(lead) ? lead : [lead];

  return (
    <section
      data-testid={testId}
      className="py-20 lg:py-28 bg-white border-b border-slate-100"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: editorial hero copy */}
          <motion.div
            className="lg:col-span-5"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <div className="lg:sticky lg:top-24">
              <p
                className="text-[#0007b5] text-sm font-semibold uppercase tracking-widest mb-5"
                data-testid={`${testId}-eyebrow`}
              >
                {eyebrow}
              </p>
              <h2
                className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-6"
                data-testid={`${testId}-headline`}
              >
                {headline}
              </h2>
              <div className="w-16 h-0.5 bg-[#0007b5] mb-6" />
              {leadParagraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-lg text-slate-600 leading-relaxed mb-4 last:mb-0"
                  data-testid={`${testId}-lead-${i}`}
                >
                  {p}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Right: stacked benefit rows — typography only */}
          <div className="lg:col-span-7">
            <ol className="space-y-0" data-testid={`${testId}-list`}>
              {benefits.map((b, i) => (
                <motion.li
                  key={b.title}
                  className="group grid grid-cols-12 gap-4 sm:gap-6 py-7 border-t border-slate-200 first:border-t-0 lg:first:border-t lg:first:pt-7"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-40px' }}
                  variants={fadeUp}
                  custom={i}
                  data-testid={`${testId}-item-${i}`}
                >
                  <div className="col-span-2 sm:col-span-1">
                    {marker === 'check' ? (
                      <CircleCheckBig
                        className="w-7 h-7 text-green-500"
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    ) : (
                      <span className="text-sm font-mono font-semibold text-[#0007b5] tabular-nums tracking-tight">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    )}
                  </div>
                  <div className="col-span-10 sm:col-span-11">
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-2 transition-colors duration-300 group-hover:text-[#0007b5]">
                      {b.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed max-w-xl">
                      {b.desc}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustBenefitsSection;
